import React, { useState } from 'react';
import { Ban, RotateCcw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { fmtQ } from './PoLinePicker';

export const isAdminUser = () => { try { return ['Admin', 'Super Admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role); } catch { return false; } };

const LINE_TONE = { RECEIVED: 'bg-green-50 text-green-700 border-green-300', PARTIAL: 'bg-amber-50 text-amber-800 border-amber-300',
  SHORT_CLOSED: 'bg-slate-100 text-slate-700 border-slate-400', PENDING: 'bg-white text-gray-600 border-gray-300' };

/** Admin: close the rest of a line with a reason — nothing more is expected and the PO can complete. */
const ShortCloseDialog = ({ item, open, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/po-receipts/lines/${item.po_item_id}/short-close`, { reason });
      toast.success(`${item.supplier_design_name} short-closed · PO is ${r.data.po_status.replace('_', ' ').toLowerCase()}`);
      setReason(''); onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not short-close'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="short-close-dialog">
        <DialogHeader>
          <DialogTitle>Short-close {item.supplier_design_name}</DialogTitle>
          <DialogDescription>Received {fmtQ(item.received_qty)} of {fmtQ(item.ordered_qty)} {item.unit}. The pending {fmtQ(item.balance_qty)} {item.unit} will no longer be expected and the sections can't count more on this line.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason — e.g. supplier can't make the balance" data-testid="short-close-reason" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || reason.trim().length < 3} className="bg-slate-800 hover:bg-slate-900" data-testid="short-close-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Ban className="w-4 h-4 mr-1" />}Short-close line</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Line status badge (with the short-close reason) and the Admin short-close / reopen actions. */
export const LineStatusCell = ({ item, poOpen, poLive, onChanged }) => {
  const [open, setOpen] = useState(false);
  const admin = isAdminUser();
  const reopen = async () => {
    if (!window.confirm(`Reopen ${item.supplier_design_name}? Its pending qty will be expected again.`)) return;
    try { await apiClient.post(`/po-receipts/lines/${item.po_item_id}/reopen`); toast.success('Line reopened'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not reopen'); }
  };
  const st = item.status || 'PENDING';
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge variant="outline" className={`text-[10px] ${LINE_TONE[st] || LINE_TONE.PENDING}`} data-testid={`po-item-status-${item.po_item_id}`}>{st.replace('_', ' ')}</Badge>
      {item.short_closed && <span className="text-[10px] text-gray-500 max-w-[180px]" data-testid={`po-item-sc-reason-${item.po_item_id}`}>
        {fmtQ(item.short_closed_qty)} {item.unit} closed · {item.short_close_reason} · {item.short_closed_by}</span>}
      {admin && poOpen && !item.short_closed && item.balance_qty > 0 && (
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-slate-700" onClick={() => setOpen(true)} data-testid={`po-item-short-close-${item.po_item_id}`}>
          <Ban className="w-3 h-3 mr-1" />Short-close</Button>)}
      {admin && poLive && item.short_closed && (
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-blue-700" onClick={reopen} data-testid={`po-item-reopen-${item.po_item_id}`}>
          <RotateCcw className="w-3 h-3 mr-1" />Reopen</Button>)}
      <ShortCloseDialog item={item} open={open} onClose={() => setOpen(false)} onDone={onChanged} />
    </div>
  );
};
